import { lazy, Suspense, useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { restaurarSesion } from '@/lib/api';
import { useAuth } from '@/lib/auth-store';
import { Layout } from '@/components/Layout';
import { Inicio } from '@/pages/Inicio';
import { Comercio } from '@/pages/Comercio';
import { Buscar } from '@/pages/Buscar';
import { Ayuda } from '@/pages/Ayuda';
import { Acceso } from '@/pages/Acceso';
import { Checkout } from '@/pages/Checkout';
import { MisPedidos } from '@/pages/MisPedidos';
import { Pedido } from '@/pages/Pedido';

/** Arrastra MapLibre: solo se descarga cuando el cliente gestiona direcciones. */
const MisDirecciones = lazy(() =>
  import('@/pages/MisDirecciones').then((m) => ({ default: m.MisDirecciones })),
);

/** Dos mapas en la misma pantalla: es la que más pesa de la tienda. */
const Encomiendas = lazy(() =>
  import('@/pages/Encomiendas').then((m) => ({ default: m.Encomiendas })),
);
const NoAliados = lazy(() =>
  import('@/pages/NoAliados').then((m) => ({ default: m.NoAliados })),
);

/** También arrastra MapLibre; solo se abre mientras el pedido está en curso. */
const Seguimiento = lazy(() =>
  import('@/pages/Seguimiento').then((m) => ({ default: m.Seguimiento })),
);

export function App() {
  const cargando = useAuth((s) => s.cargando);

  /**
   * Al cargar la página se intenta recuperar la sesión con la cookie de
   * refresh. La tienda es pública, así que si no hay sesión simplemente se
   * navega como visitante.
   */
  useEffect(() => {
    void restaurarSesion();
  }, []);

  if (cargando) return <Cargando />;

  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<Inicio />} />
        <Route path="/c/:slug" element={<Comercio />} />
        <Route path="/buscar" element={<Buscar />} />
        {/* Pública: quien todavía no tiene cuenta también necesita entenderlo. */}
        <Route path="/ayuda" element={<Ayuda />} />
        {/*
          Visible sin sesión a propósito.
          Quien no tiene cuenta también necesita descubrir que esto existe y
          cuánto cuesta; el botón de enviar es el que exige entrar.
        */}
        <Route
          path="/encomiendas"
          element={
            <Suspense fallback={<Cargando />}>
              <Encomiendas />
            </Suspense>
          }
        />
        <Route
          path="/no-aliados"
          element={
            <SoloClientes>
              <Suspense fallback={<Cargando />}>
                <NoAliados />
              </Suspense>
            </SoloClientes>
          }
        />
        <Route
          path="/mis-direcciones"
          element={
            <SoloClientes>
              <Suspense fallback={<Cargando />}>
                <MisDirecciones />
              </Suspense>
            </SoloClientes>
          }
        />
        <Route
          path="/checkout"
          element={
            <SoloClientes>
              <Checkout />
            </SoloClientes>
          }
        />
        <Route
          path="/pedidos"
          element={
            <SoloClientes>
              <MisPedidos />
            </SoloClientes>
          }
        />
        <Route
          path="/pedidos/:id"
          element={
            <SoloClientes>
              <Pedido />
            </SoloClientes>
          }
        />
        <Route path="*" element={<NoEncontrado />} />
      </Route>

      {/* Fuera del layout: el mapa ocupa la pantalla completa. */}
      <Route
        path="/pedidos/:id/mapa"
        element={
          <SoloClientes>
            <Suspense fallback={<Cargando />}>
              <Seguimiento />
            </Suspense>
          </SoloClientes>
        }
      />

      {/* Fuera del layout: la pantalla de acceso ocupa todo el alto. */}
      <Route path="/acceso" element={<AccesoOInicio />} />
    </Routes>
  );
}

function AccesoOInicio() {
  const accessToken = useAuth((s) => s.accessToken);
  return accessToken ? <Navigate to="/" replace /> : <Acceso />;
}

/** Comodidad de navegación: la autorización real la impone la API. */
function SoloClientes({ children }: { children: React.ReactNode }) {
  const accessToken = useAuth((s) => s.accessToken);
  return accessToken ? <>{children}</> : <Navigate to="/acceso" replace />;
}

function Cargando() {
  return (
    <div className="grid min-h-dvh place-items-center bg-kick-light">
      <div className="flex flex-col items-center gap-3">
        <img src="/logo.png" alt="Kick Domicilios" className="h-14 w-auto opacity-90" />
        <Loader2 className="h-6 w-6 animate-spin text-kick-red" />
      </div>
    </div>
  );
}

function NoEncontrado() {
  return (
    <div className="grid min-h-[60vh] place-items-center px-4 text-center">
      <div>
        <p className="font-display text-7xl text-kick-red">404</p>
        <h1 className="mt-2 text-2xl text-kick-dark">Página no encontrada</h1>
        <a href="/" className="btn-kick mt-6">
          Ver comercios
        </a>
      </div>
    </div>
  );
}
